import { motion } from "framer-motion";

const blocks = [
  {
    title: "Manufacturing & Standards",
    items: [
      "Founded in 2005, manufacturing solid tyres and wheel assemblies for material-handling equipment.",
      "Products designed to ETRTO and US-TRA standards, supporting REACH compliance.",
      "ISO 9001:2015-certified quality-management system.",
    ],
  },
  {
    title: "Applications",
    items: [
      "Material handling — forklifts, warehouse and logistics fleets.",
      "Construction and mining equipment.",
      "Industrial off-highway applications requiring dependable solid-tyre solutions.",
    ],
  },
  {
    title: "OMC LTD × IRC Value",
    items: [
      "Reliable solid tyres for demanding industrial applications.",
      "Consistent product quality and performance.",
      "Competitive aftermarket solutions.",
      "Responsive UK and European commercial support.",
      "Long-term supply and distribution relationships.",
    ],
  },
];

export default function IRCSection({ variant = "default" }) {
  return (
    <section
      data-testid="irc-section"
      className={`relative py-20 md:py-28 ${
        variant === "alt" ? "bg-[#B8CCE0]" : "bg-white"
      } border-y border-[#D5DEE8]`}
    >
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 lg:px-14">
        <div className="mb-10 md:mb-14 max-w-3xl">
          <p className="overline-maroon mb-4">Manufacturing Partnerships</p>
          <h2 className="font-serif-display font-light text-3xl md:text-4xl lg:text-5xl leading-tight tracking-tight text-[#0F1E33]">
            Expanding our <span className="italic text-[#1E3A5F]">manufacturing partnerships</span>.
          </h2>
          <div className="hairline-gold w-24 mt-5" />
          <p className="mt-5 text-[#1F2D40] text-base md:text-lg leading-relaxed">
            OMC LTD continues to strengthen its product portfolio through
            relationships with established manufacturers that share our
            commitment to quality, reliability and long-term customer support.
          </p>
        </div>

        <div className="grid grid-cols-12 gap-8 md:gap-12 items-start">
          {/* Left: Logo + intro */}
          <div className="col-span-12 lg:col-span-5">
            <p className="overline-maroon mb-4">Partner</p>

            <div
              data-testid="irc-logo"
              className="inline-block bg-[#0F1E33] border border-[#D5DEE8] p-6 md:p-8 shadow-sm"
            >
              <img
                src="/brand/irc-logo.webp"
                alt="Industrial Rubber Company — IRC Tyre"
                className="h-14 md:h-16 w-auto object-contain"
              />
            </div>

            <h3 className="mt-7 font-serif-display font-light text-3xl md:text-4xl lg:text-5xl leading-[1.05] tracking-tight text-[#0F1E33]">
              OMC LTD <span className="italic text-[#1E3A5F]">× IRC Tyre</span>
              <span className="block text-lg md:text-xl font-mono tracking-[0.3em] uppercase text-[#B8860B] mt-3">
                SOLIDO — Solid Tyre Brand
              </span>
            </h3>
            <div className="hairline-gold w-20 mt-5" />
            <p className="mt-5 text-[#1F2D40] text-base md:text-lg leading-relaxed max-w-xl">
              We are pleased to be working with{" "}
              <strong className="text-[#0F1E33]">Industrial Rubber Company — IRC Tyre</strong>,
              an experienced manufacturer of solid tyres and wheel assemblies
              for material-handling equipment — sold under their <strong className="text-[#0F1E33]">SOLIDO</strong> brand. This
              collaboration expands our offering of dependable solid-tyre
              solutions to distributors, fleet operators and industrial
              customers across the UK and Europe.
            </p>
          </div>

          {/* Right: 3 info blocks */}
          <div className="col-span-12 lg:col-span-7 space-y-5">
            {blocks.map((b, idx) => (
              <motion.div
                key={b.title}
                data-testid={`irc-block-${idx}`}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.5, delay: idx * 0.08 }}
                className={`${
                  variant === "alt" ? "bg-white" : "bg-[#D6E2F0]"
                } border border-[#D5DEE8] p-6 md:p-7 hover:border-[#B8860B] transition-colors`}
              >
                <div className="flex items-baseline gap-3 mb-3">
                  <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#B8860B]">
                    {String(idx + 1).padStart(2, "0")}
                  </span>
                  <h4 className="font-serif-display text-xl md:text-2xl text-[#0F1E33] tracking-tight">
                    {b.title}
                  </h4>
                </div>
                <ul className="space-y-2 md:space-y-2.5">
                  {b.items.map((it) => (
                    <li key={it} className="flex gap-3 items-start">
                      <span className="mt-2 w-1.5 h-1.5 bg-[#1E3A5F] shrink-0" />
                      <span className="text-sm md:text-base text-[#1F2D40] leading-relaxed">
                        {it}
                      </span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>

        <p className="mt-10 md:mt-14 max-w-3xl text-[#1F2D40] text-sm md:text-base leading-relaxed italic border-l-2 border-[#B8860B] pl-5">
          This collaboration supports our wider objective of becoming a trusted
          sourcing and distribution partner for automotive, commercial and
          off-highway components throughout the UK and Europe.
        </p>
      </div>
    </section>
  );
}
